import { useCallback, useRef, useState } from 'react'
import {
  VersionReportVersionEntity,
  VersionReportVersionPageEntity,
} from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { useVersionReportVersionRepository } from '../../../../services/adaptors/versionReportVersionRepositoryAdaptor'

const hasPageChanged = (
  original: VersionReportVersionPageEntity,
  current: VersionReportVersionPageEntity
): boolean => {
  return (
    original.pageTitle !== current.pageTitle ||
    original.leadSentence !== current.leadSentence ||
    JSON.stringify(original.config) !== JSON.stringify(current.config) ||
    JSON.stringify(original.content) !== JSON.stringify(current.content)
  )
}

const getChangedPages = (
  serverVersion: VersionReportVersionEntity,
  localVersion: VersionReportVersionEntity
): VersionReportVersionPageEntity[] => {
  return serverVersion.pages.filter(serverPage => {
    const localPage = localVersion.pages.find(
      page => page.uuid === serverPage.uuid
    )
    if (!localPage) return true
    return hasPageChanged(serverPage, localPage)
  })
}

export type StalePageInfo = {
  uuid: string
  pageNumber: number
  pageTitle: string
}

export const useEditingState = (
  currentVersion: VersionReportVersionEntity | undefined,
  currentPage: VersionReportVersionPageEntity | undefined,
  updateCurrentVersion?: (version: VersionReportVersionEntity) => void
) => {
  const [editing, setEditing] = useState<boolean>(false)
  const [staleWarningDialogOpen, setStaleWarningDialogOpen] =
    useState<boolean>(false)
  const [stalePages, setStalePages] = useState<StalePageInfo[]>([])
  const originalPagesRef = useRef<VersionReportVersionPageEntity[]>([])
  const latestVersionRef = useRef<VersionReportVersionEntity | undefined>()

  const { fetch, updatePages } = useVersionReportVersionRepository()

  const proceedEditing = useCallback(() => {
    if (!currentVersion) return
    originalPagesRef.current = JSON.parse(JSON.stringify(currentVersion.pages))
    latestVersionRef.current = undefined
    setStalePages([])
    setStaleWarningDialogOpen(false)
    setEditing(true)
  }, [currentVersion])

  const proceedWithLatestData = useCallback(() => {
    if (!latestVersionRef.current || !updateCurrentVersion) return
    updateCurrentVersion(latestVersionRef.current)
    originalPagesRef.current = JSON.parse(
      JSON.stringify(latestVersionRef.current.pages)
    )
    latestVersionRef.current = undefined
    setStalePages([])
    setStaleWarningDialogOpen(false)
    setEditing(true)
  }, [updateCurrentVersion])

  const closeStaleWarningDialog = useCallback(() => {
    latestVersionRef.current = undefined
    setStalePages([])
    setStaleWarningDialogOpen(false)
  }, [])

  const startEditing = useCallback(async () => {
    if (!currentPage || !currentVersion) return

    const latestVersion = await fetch(currentVersion.uuid)
    const changedPages = getChangedPages(latestVersion, currentVersion)

    if (changedPages.length > 0) {
      latestVersionRef.current = latestVersion
      setStalePages(
        changedPages.map(page => {
          const pageIndex = latestVersion.pages.findIndex(
            p => p.uuid === page.uuid
          )
          return {
            uuid: page.uuid,
            pageNumber: pageIndex + 1,
            pageTitle: page.pageTitle,
          }
        })
      )
      setStaleWarningDialogOpen(true)
      return
    }

    originalPagesRef.current = JSON.parse(JSON.stringify(currentVersion.pages))
    setEditing(true)
  }, [currentPage, currentVersion, fetch])

  const finishEditing = useCallback(async () => {
    if (!currentVersion) return

    const changedPages = currentVersion.pages.filter(page => {
      const originalPage = originalPagesRef.current.find(
        original => original.uuid === page.uuid
      )
      if (!originalPage) return true
      return hasPageChanged(originalPage, page)
    })

    if (changedPages.length > 0) {
      const versionWithChangedPages: VersionReportVersionEntity = {
        ...currentVersion,
        pages: changedPages,
      }
      await updatePages(versionWithChangedPages)
    }

    originalPagesRef.current = []
    setEditing(false)
  }, [currentVersion, updatePages])

  return {
    editing,
    startEditing,
    finishEditing,
    staleWarningDialogOpen,
    stalePages,
    proceedEditing,
    proceedWithLatestData,
    closeStaleWarningDialog,
  }
}
